import { useEffect } from "react";
import { fetchConversationMessages } from "../../../../services/conversationHistoryService";
import { ChatStreamError } from "../../../../services/chatService";
import { Message } from "../types";
import {
  mapConversationHistoryToMessages,
  overlayCachedBlogState,
  overlayCachedOutreachEmailState,
} from "../utils/historyMessageMapper";
import { debug } from "../../../../utils/debugLogger";

type UseThreadHistorySyncParams = {
  threadIdProp?: string | null;
  threadId: string | null;
  idToken: string;
  setThreadId: (threadId: string | null) => void;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  setStreamingContent: (value: string) => void;
  setStreamingActions: (value: any) => void;
  setStreamingUiActions: (value: any) => void;
  setStreamingCitations: (value: any) => void;
  setStarterActions: (value: any) => void;
  setStreamingGeneratedImages: (value: any) => void;
  setStreamingGeneratedPosts: (value: any) => void;
  setError: (value: ChatStreamError | string | null) => void;
  setIsLoading: (value: boolean) => void;
};

export function useThreadHistorySync({
  threadIdProp,
  threadId,
  idToken,
  setThreadId,
  setMessages,
  setStreamingContent,
  setStreamingActions,
  setStreamingUiActions,
  setStreamingCitations,
  setStarterActions,
  setStreamingGeneratedImages,
  setStreamingGeneratedPosts,
  setError,
  setIsLoading,
}: UseThreadHistorySyncParams): void {
  useEffect(() => {
    const resetTransientState = () => {
      setMessages([]);
      setStreamingContent("");
      setStreamingActions(null);
      setStreamingUiActions(null);
      setStreamingCitations(null);
      setStarterActions(null);
      setStreamingGeneratedImages(null);
      setStreamingGeneratedPosts(null);
      setError(null);
    };

    const loadThreadMessages = async () => {
      if (threadIdProp === threadId) return;

      setThreadId(threadIdProp || null);

      if (!threadIdProp) {
        resetTransientState();
        return;
      }

      try {
        resetTransientState();
        setIsLoading(true);
        const messageHistory = await fetchConversationMessages(threadIdProp, idToken || "");
        let formattedMessages = mapConversationHistoryToMessages(
          messageHistory as any[],
          threadIdProp
        );

        // Blogs are now persisted server-side (in position), but a blog's post-generation
        // state (Saved id / Published wordpressUrl) lives only in the per-thread cache.
        // Overlay that cached state onto the matching server blog, and append any cached
        // blog the server doesn't have (legacy threads created before blogs were persisted).
        try {
          const cachedRaw = localStorage.getItem(`wp_blog_thread_${threadIdProp}`);
          if (cachedRaw) {
            const cachedBlogs = JSON.parse(cachedRaw);
            if (Array.isArray(cachedBlogs) && cachedBlogs.length > 0) {
              formattedMessages = overlayCachedBlogState(formattedMessages, cachedBlogs as Message[]);
            }
          }
        } catch {
          /* ignore malformed/unavailable cache */
        }

        // Outreach email drafts (SGL-83): edits and Approve are UI-only state (no
        // backend write), so the per-thread cache is the only place they survive a
        // refresh — overlay it the same way as the blog cache above.
        try {
          const cachedOutreachRaw = localStorage.getItem(`outreach_email_thread_${threadIdProp}`);
          if (cachedOutreachRaw) {
            const cachedOutreach = JSON.parse(cachedOutreachRaw);
            if (Array.isArray(cachedOutreach) && cachedOutreach.length > 0) {
              formattedMessages = overlayCachedOutreachEmailState(formattedMessages, cachedOutreach as Message[]);
            }
          }
        } catch {
          /* ignore malformed/unavailable cache */
        }

        setMessages(formattedMessages);
      } catch (err) {
        debug.error("NewChatWindow: Failed to load message history:", err);
        setError("Failed to load conversation history");
      } finally {
        setIsLoading(false);
      }
    };

    void loadThreadMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadIdProp, idToken]);
}
